import { mediaSha256 } from "../media-hash";
import { el, routeLink, topbar } from "../lib";
import { mountPdfPreview, type PdfPreviewHandle } from "../pdf-preview";
import { PATHS } from "../../shared/navigation";
import { parseRazManifest } from "../../shared/raz";
import { buildPageSegments, pageAtTime, parseRazSync, type PageMarker, type RazSync } from "../../shared/raz-sync";

async function mediaData(key: string): Promise<{ data: ArrayBuffer; sha256: string }> {
  const sign = await fetch(`/api/sign?material=raz&k=${encodeURIComponent(key)}`);
  if (!sign.ok) throw new Error("无法获取媒体地址");
  const { url } = await sign.json() as { url: string };
  const response = await fetch(url);
  if (!response.ok) throw new Error("媒体加载失败");
  const data = await response.arrayBuffer();
  return { data, sha256: await mediaSha256(data) };
}

export async function razAnnotateView(root: HTMLElement): Promise<void> {
  document.title = "RAZ 页码打标";
  root.appendChild(topbar([{ label: "英语", href: PATHS.english }, { label: "RAZ A 级", href: PATHS.razA }, { label: "页码打标" }]));
  const main = el("main", "raz-annotator");
  root.appendChild(main);
  const message = el("p", "hint", "正在加载绘本和音频…");
  main.appendChild(message);
  try {
    const response = await fetch("/api/raz/manifest");
    if (!response.ok) throw new Error("书目加载失败");
    const manifest = parseRazManifest(await response.json());
    const id = new URLSearchParams(location.search).get("id") ?? "01";
    const book = manifest.books.find(b => b.id === id);
    if (!book?.pdf || !book.audio) throw new Error("这篇材料没有同时提供 PDF 和音频，无法打标");
    const [pdfMedia, audioMedia] = await Promise.all([mediaData(book.pdf), mediaData(book.audio)]);
    if (!main.isConnected) return;
    message.remove();
    const pdfUrl = URL.createObjectURL(new Blob([pdfMedia.data], { type: "application/pdf" }));
    const audioUrl = URL.createObjectURL(new Blob([audioMedia.data], { type: "audio/mpeg" }));
    const preview = el("section", "annotation-preview");
    const panel = el("section", "annotation-panel");
    main.append(preview, panel);
    panel.appendChild(el("h2", "section-title", `${book.id} ${book.title} · 页码打标`));
    panel.appendChild(el("p", "hint", "优先点“加载 ASR 自动打标”，再试听自动翻页。若需校正，修改对应秒数即可；“标记本页开始”和“标记朗读结束”用于补充或修正异常。"));
    const readerLink = routeLink(`${PATHS.razReader}?id=${encodeURIComponent(id)}`);
    readerLink.textContent = "返回普通阅读";
    panel.appendChild(readerLink);
    const audio = document.createElement("audio");
    audio.src = audioUrl; audio.controls = true; audio.preload = "auto";
    panel.appendChild(audio);
    const speed = document.createElement("select");
    speed.setAttribute("aria-label", "打标播放速度");
    for (const rate of [0.5, 0.75, 1]) {
      const option = document.createElement("option");
      option.value = String(rate); option.textContent = `${rate} 倍速`; option.selected = rate === 1;
      speed.appendChild(option);
    }
    speed.addEventListener("change", () => { audio.playbackRate = Number(speed.value); });
    panel.appendChild(speed);
    const clock = el("p", "annotation-clock");
    clock.setAttribute("aria-live", "off");
    panel.appendChild(clock);
    const actions = el("div", "annotation-actions");
    panel.appendChild(actions);
    function button(label: string, action: () => void) {
      const b = el("button", "pdf-button", label) as HTMLButtonElement;
      b.type = "button"; b.addEventListener("click", action); actions.appendChild(b); return b;
    }
    const feedback = el("p", "hint");
    feedback.setAttribute("role", "status");
    panel.appendChild(feedback);
    const rows = el("div", "annotation-markers");
    panel.appendChild(rows);
    let currentPage = 1;
    let pageCount = 0;
    let markers: PageMarker[] = [];
    let end: number | null = null;
    let following = false;
    let requestedPage: number | null = null;
    let handle: PdfPreviewHandle;
    const draftKey = `raz-calibration-v1:${id}:${pdfMedia.sha256}:${audioMedia.sha256}`;
    function save() {
      try { localStorage.setItem(draftKey, JSON.stringify({ markers, end })); feedback.textContent = "草稿已保存在当前浏览器；导出后才会生成文件。"; }
      catch { feedback.textContent = "浏览器无法保存草稿，请及时导出文件。"; }
    }
    try {
      const draft = JSON.parse(localStorage.getItem(draftKey) ?? "null");
      if (draft && Array.isArray(draft.markers) && draft.markers.every((m: PageMarker) => m && Number.isInteger(m.page) && Number.isFinite(m.start)) && (draft.end === null || Number.isFinite(draft.end))) {
        markers = draft.markers; end = draft.end;
        feedback.textContent = "已恢复这组 PDF 和音频的打标草稿。";
      }
    } catch { /* 存储不可用或草稿损坏不影响新建标注。 */ }
    function stopFollowing() { requestedPage = null; following = false; audition.textContent = "试听自动翻页"; }
    function clockText() { clock.textContent = `PDF 第 ${currentPage} / ${pageCount || "…"} 页 · 音频 ${audio.currentTime.toFixed(2)} 秒`; }
    function input(label: string, value: number, change: (n: number) => void, step: string) {
      const field = document.createElement("input");
      field.type = "number"; field.min = "0"; field.step = step; field.value = String(value); field.setAttribute("aria-label", label);
      field.addEventListener("change", () => { change(field.value === "" ? NaN : Number(field.value)); stopFollowing(); save(); });
      return field;
    }
    function renderRows() {
      rows.replaceChildren();
      markers.forEach((m, index) => {
        const row = el("div", "annotation-marker");
        row.appendChild(el("span", "", `#${index + 1}`));
        row.appendChild(input("PDF 页序号", m.page, n => { m.page = n; }, "1"));
        row.appendChild(input("开始秒数", m.start, n => { m.start = n; }, "0.05"));
        const listen = el("button", "pdf-button", "回听") as HTMLButtonElement;
        listen.addEventListener("click", () => {
          if (!Number.isFinite(m.start) || m.start < 0 || m.start >= audio.duration) { feedback.textContent = "请先修正开始秒数"; return; }
          stopFollowing(); handle.goToPage(m.page); audio.currentTime = Math.max(0, m.start - 0.5);
          void audio.play().catch(() => { feedback.textContent = "请点击音频播放按钮"; });
        });
        const remove = el("button", "pdf-button", "删除") as HTMLButtonElement;
        remove.addEventListener("click", () => { stopFollowing(); markers.splice(index, 1); save(); renderRows(); });
        row.append(listen, remove); rows.appendChild(row);
      });
      if (end !== null) {
        const row = el("label", "annotation-end", "朗读结束秒数 ");
        row.appendChild(input("朗读结束秒数", end, n => { end = n; }, "0.05"));
        rows.appendChild(row);
      }
    }
    button("标记本页开始", () => {
      stopFollowing();
      if (!pageCount || !Number.isFinite(audio.duration)) { feedback.textContent = "请等待绘本和音频加载完成"; return; }
      const time = Math.round(audio.currentTime * 1000) / 1000;
      if (markers.length && time <= markers[markers.length - 1].start) { feedback.textContent = "新标记必须晚于上一条；修正已有标记请直接修改秒数。"; return; }
      markers.push({ page: currentPage, start: time }); end = null; save(); renderRows();
    });
    button("标记朗读结束", () => {
      stopFollowing(); audio.pause(); end = Math.round(audio.currentTime * 1000) / 1000; save(); renderRows();
    });
    const audition = button("试听自动翻页", () => {
      if (following) { stopFollowing(); audio.pause(); return; }
      try {
        buildPageSegments(markers, end ?? NaN, pageCount, audio.duration);
        following = true; audition.textContent = "停止试听";
        audio.currentTime = markers[0].start;
        updateFollow();
        void audio.play().catch(() => { stopFollowing(); feedback.textContent = "请点击音频播放按钮"; });
      } catch (err) { feedback.textContent = (err as Error).message; }
    });
    function updateFollow() {
      clockText();
      if (!following || end === null) return;
      if (audio.currentTime >= end) { audio.pause(); stopFollowing(); return; }
      const page = pageAtTime(buildPageSegments(markers, end, pageCount, audio.duration), audio.currentTime);
      if (page !== null && page !== currentPage && page !== requestedPage) { requestedPage = page; handle.goToPage(page); }
    }
    audio.addEventListener("timeupdate", updateFollow);
    audio.addEventListener("seeked", updateFollow);
    audio.addEventListener("ended", stopFollowing);
    audio.addEventListener("loadedmetadata", clockText);
    button("导出时间轴 JSON", () => {
      try {
        const segments = buildPageSegments(markers, end ?? NaN, pageCount, audio.duration);
        const sync: RazSync = {
          version: 1, level: "A", bookId: id,
          pdf: { key: book.pdf!, sha256: pdfMedia.sha256, pageCount },
          audio: { key: book.audio!, sha256: audioMedia.sha256, duration: audio.duration }, segments,
        };
        const url = URL.createObjectURL(new Blob([JSON.stringify(sync, null, 2) + "\n"], { type: "application/json" }));
        const link = document.createElement("a"); link.href = url; link.download = `${id}-sync.json`; link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        feedback.textContent = "已导出；未上传或发布，请先试听确认。";
      } catch (err) { feedback.textContent = (err as Error).message; }
    });
    button("加载 ASR 自动打标", () => {
      void (async () => {
        try {
          if (markers.length) throw new Error("当前已有草稿；为避免覆盖，请先导出并保留现有标记。自动结果可在清空本书草稿后加载。");
          if (!pageCount) throw new Error("请等待 PDF 加载完成");
          const response = await fetch(`/raz-sync-candidates/A/${encodeURIComponent(id)}.json`);
          if (!response.ok) throw new Error("这篇还没有自动对齐结果");
          const sync = parseRazSync(await response.json());
          if (sync.bookId !== id || sync.pdf.sha256 !== pdfMedia.sha256 || sync.audio.sha256 !== audioMedia.sha256 || sync.pdf.pageCount !== pageCount || Math.abs(sync.audio.duration - audio.duration) > 0.25) throw new Error("自动结果与当前 PDF 或音频版本不一致");
          if (sync.segments.some((s, i) => i > 0 && s.start !== sync.segments[i - 1].end)) throw new Error("此自动结果含时间空档，当前校准工具不能无损编辑");
          stopFollowing();
          markers = sync.segments.map(s => ({ page: s.page, start: s.start }));
          end = sync.segments[sync.segments.length - 1].end;
          save(); renderRows();
          feedback.textContent = "已加载 ASR 候选时间轴，请点“试听自动翻页”检查，无需逐页从头打标。";
        } catch (err) { feedback.textContent = (err as Error).message; }
      })();
    });
    handle = mountPdfPreview(preview, pdfUrl, book.title, undefined, {
      onPageChange(page, total) { currentPage = page; pageCount = total; requestedPage = null; clockText(); },
    });
    clockText(); renderRows();
    const observer = new MutationObserver(() => {
      if (main.isConnected) return;
      observer.disconnect(); audio.pause(); handle(); URL.revokeObjectURL(pdfUrl); URL.revokeObjectURL(audioUrl);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  } catch (err) {
    if (main.isConnected) { message.textContent = (err as Error).message; }
  }
}
